import { useEffect, useRef, useState } from 'react';
import { api } from '../../shell/bridge';
import type { Role } from '../../lib/api';
import { reportActionFailure } from '../../lib/actionFeedback';

/**
 * Two-ness: role A = bottle green, role B (or unknown, before members load) =
 * blush. Colours come from `--color-partner-a/b` instead of the hex literals
 * that lived in main.ts.
 */
export function Bubble({ text, mine, role, from, seq, colour, onRemoveMessage }: { text: string; mine: boolean; role: Role | null; from?: string | null; seq?: number; colour?: string | null; onRemoveMessage?: (seq: number) => void }) {
  /* One delete path for both affordances (card — msg-remove). Confirm first:
     the row vanishes for BOTH partners, so a stray tap must not remove
     without a native yes/no. */
  const confirmDelete = () => {
    if (seq == null || !onRemoveMessage) return;
    if (!window.confirm('Delete this message for you both?')) return;
    onRemoveMessage(seq);
  };

  /* Card CJQIKjFiNUCW — the controls are an in-message popup, not a strip
     under the bubble. Hover reveals them on a fine pointer; touch has no
     hover, so a tap or long-press sets the same open state explicitly. */
  const [actionsOpen, setActionsOpen] = useState(false);
  /* Card pP0wDgwED4h5: the actions popup is placed by measurement, not by
     guesswork. Founder email 2026-10-30 put it at the bottom LEFT of the
     message, so it opens below the bubble and only flips above when the bottom
     of the viewport would clip it (the last messages in the log). One rule on
     both message sides. */
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<'above' | 'below'>('below');

  useEffect(() => {
    if (!actionsOpen) return;
    const acts = bubbleRef.current?.querySelector('.pear-msg-acts');
    if (!(acts instanceof HTMLElement)) return;
    setPlacement(
      acts.getBoundingClientRect().bottom > window.innerHeight - 8 ? 'above' : 'below',
    );
  }, [actionsOpen]);

  useEffect(() => {
    if (!actionsOpen) return;
    const onDown = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('.pear-msg-acts')) return;
      setActionsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setActionsOpen(false); };
    const onScroll = () => setActionsOpen(false);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [actionsOpen]);

  const hasActions = seq != null;

  return (
    <div
      className={`pear-msg ${mine ? 'pear-msg-mine' : 'pear-msg-other'}`}
      data-mine={String(mine)}
      data-role={role ?? ''}
      data-from={from ?? ''}
      data-reply-colour={colour ?? ''}
      data-actions={actionsOpen ? 'open' : 'closed'}
      /* Archive jump target (card 2sjo3uNvaRDr): the one handle a jump can
         find after the history page lands. Kept on the row, not the bubble,
         so the highlight covers the whole message. */
      data-msg-seq={seq ?? ''}
      /* Long-press / right-click OPENS the popup (card CJQIKjFiNUCW). It used
         to fire the delete confirm straight off contextmenu, which made a held
         press a destructive shortcut with no visible control. Partner messages
         keep the platform menu — there is nothing to remove there. */
      onContextMenu={hasActions ? (e) => { e.preventDefault(); setActionsOpen(true); } : undefined}
    >
      {from && <span className="pear-msg-from">{from}</span>}
      {/* Card 5KtSIi7Gz8tb (founder email 2026-10-18): the thread reads WHO
          spoke, not which role slot — my words on white, the partner's on
          gold. Both pairs are AA on the velvet ground (cream 11.9:1,
          mustard 5.5:1 against bottle-green ink). data-role stays for the
          probes and the name label above.

          Card 9QADJv3CdiOh: the partner's bubble also wears the ink THEY
          picked (Settings > You). Every offered colour is measured AA on the
          same mustard ground, and the inline colour only lands when a real
          hex arrived — unknown/absent falls back to the token pair untouched. */}
      <div
        ref={bubbleRef}
        className={`pear-msg-bubble ${mine ? 'pear-msg-bubble-mine' : 'pear-msg-bubble-theirs'}`}
        style={colour ? { color: colour } : undefined}
        onClick={
          /* Touch only: a tap on a bubble toggles the popup. On a fine pointer
             hover already owns the reveal, and a click-to-open there would
             fight the text selection.

             Founder email 2026-10-27: this used to be gated on `mine`, so a
             PARTNER's message could no longer be tapped to open it — and pin
             (save to Notebook) is exactly what you want on the other person's
             message. Delete stays scoped to your own bubble below; the popup
             itself is now reachable on either side. */
          hasActions && typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches
            ? (e) => { e.stopPropagation(); setActionsOpen(v => !v); }
            : undefined
        }
      >
        {text}
        {/* Founder email 2026-10-27 (also): with hover there is no affordance
            on touch, so a coarse pointer gets one always-visible "more" chip in
            the bubble's corner. It is display:none on a fine pointer, where
            hover does the job, and it disappears once the popup is open. */}
        {hasActions && !actionsOpen && (
          <button
            type="button"
            className="pear-msg-more"
            aria-label="Message actions"
            aria-expanded={actionsOpen}
            onClick={(e) => { e.stopPropagation(); setActionsOpen(true); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
              <circle cx="5" cy="12" r="1.6" />
              <circle cx="12" cy="12" r="1.6" />
              <circle cx="19" cy="12" r="1.6" />
            </svg>
          </button>
        )}
        {/* The popup itself (card CJQIKjFiNUCW): icon-only, overlays the
            bubble's top corner so the bubble never resizes, one X vector for
            delete on touch, pin (save to Notebook) beside it. Delete keeps its
            confirm step and the DO still refuses anything not mine.

            Card pP0wDgwED4h5 (founder email 2026-10-28): the menu used to sit
            INSIDE the bubble, so it clipped its own labels against the bubble
            edge and moved between message types. It now hangs off the bubble's
            right edge — same anchor on mine and theirs — and flips below when
            there is no room above (data-placement, measured on open). */}
        {hasActions && (
          <span
            className="pear-msg-acts"
            role="group"
            aria-label="Message actions"
            data-placement={placement}
          >
            <MessageSave seq={seq as number} text={text} />
            {mine && onRemoveMessage && (
              <button
                type="button"
                className="pear-msg-act pear-msg-delete"
                aria-label="Delete your message"
                title="Delete"
                onClick={(e) => { e.stopPropagation(); confirmDelete(); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}


/**
 * Save a message to the Notebook (card ERmAXm2aLjjV). Two shapes off the same
 * server route, which already owns the unfurl pipeline and the vault-gated
 * classification:
 *   - the text carries a URL -> POST {url}, the link path (links are shared)
 *   - otherwise             -> POST {entryType:'note', body, shared}
 *     where shared:false is 'me' and shared:true is 'us'.
 * Local UI state only — nothing records which messages were saved.
 */
export function MessageSave({ seq, text }: { seq: number; text: string }) {
  const [phase, setPhase] = useState<'idle' | 'choosing' | 'busy' | 'saved' | 'error'>('idle');
  const [error, setError] = useState('');
  const url = /\bhttps?:\/\/\S+/.exec(text)?.[0] ?? null;

  const save = async (visibility: 'us' | 'me') => {
    setPhase('busy');
    setError('');
    try {
      if (url) {
        await api('/api/notebook', { body: { url } });
      } else {
        // Card wGXKzRwJE57v — thread saves carry their real origin so the
        // Notebook card renders "from the thread" only for genuinely
        // thread-sourced items, not every typed card. (The {url} link path
        // below already tags itself through enrich/provider detection.)
        await api('/api/notebook', { body: { entryType: 'note', body: text, shared: visibility === 'us', provider: 'thread' } });
      }
      setPhase('saved');
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : '');
      setPhase('error');
      console.error('[Pear] save to notebook failed', err);
      reportActionFailure('save that to the Notebook');
    }
  };

  if (phase === 'saved') {
    return <span className="pear-msg-saved" data-msg-saved={seq}>Saved to Notebook</span>;
  }
  if (phase === 'error') {
    return (
      <span className="pear-msg-save-error" data-msg-save-error={seq}>
        Could not save{error ? ` (${error})` : ''}
        <button type="button" className="pear-msg-save" onClick={() => setPhase('choosing')}>Try again</button>
      </span>
    );
  }
  if (phase === 'busy') {
    return <span className="pear-msg-save-busy" data-msg-saving={seq}>Saving…</span>;
  }
  if (phase === 'choosing') {
    // One URL, one path: the link route is always shared, so a link message
    // offers no "just me" option.
    return (
      <span className="pear-msg-save-choice" data-msg-save-choice={seq}>
        {url ? (
          <button type="button" className="pear-msg-save-opt" onClick={() => void save('us')}>
            Save link to Notebook
          </button>
        ) : (
          <>
            <button type="button" className="pear-msg-save-opt" onClick={() => void save('us')}>Save for us</button>
            <button type="button" className="pear-msg-save-opt" onClick={() => void save('me')}>Just you</button>
          </>
        )}
        <button type="button" className="pear-msg-save-cancel" onClick={() => setPhase('idle')}>Cancel</button>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="pear-msg-save"
      data-msg-save={seq}
      aria-label="Save to Notebook"
      title="Save to Notebook"
      onClick={() => setPhase('choosing')}
    >
      {/* Card QR9phRSRQHNx — the save affordance is a pushpin glyph, not the
          word "Save"; every icon in the app is a vector (card svmgRALhXjiq). */}
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M12 17v5" />
        <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
      </svg>
    </button>
  );
}
